/**
 * Cấu hình → Cài đặt tiệm → hai thẻ riêng (chỉ chủ tiệm):
 *  1. "Tài khoản nhận tiền" (v0.23.2, docs/plans/tai-khoan-nhan-tien-the-rieng.md) — luôn hiện, KHÔNG nằm sau công tắc cọc: QR lúc thu
 *     (L4-04) và tiền cọc (L4-19) cùng đọc một tài khoản. Khai lần đầu hoặc đổi sang tài khoản khác phải gõ lại mã đăng nhập.
 *  2. "Đặt cọc giữ chỗ" (N2 — docs/plans/pv2-n2-coc.md). Mặc định TẮT. Bật cần số tiền + tiệm đã có tài khoản nhận.
 * Tiền vào thẳng tài khoản của tiệm — SAPA không giữ tiền. Hai thẻ cùng một lần GET; thẻ cọc luôn gửi `taiKhoan: null` (giữ nguyên),
 * thẻ tài khoản đọc lại GET ngay trước khi lưu (xem `tai-khoan-nhan-tien.ts`).
 */
import { useCallback, useEffect, useState } from 'react'
import { type Api, ApiFailure, type CauHinhCoc } from './api.ts'
import { OTien, useKhoaIdem } from './GoiVi.tsx'
import {
  canMaKhiLuu,
  cocDaDoi,
  coTheBatCoc,
  coTheLuuTaiKhoan,
  type FormCoc,
  taiKhoanGoDo,
  thanLuuCoc,
  thanLuuTaiKhoan,
} from './tai-khoan-nhan-tien.ts'
import { T } from './text/vi.ts'
import { CacCum, CongTac, ThongBao } from './ui.tsx'

const GIU_CHON = [15, 30, 60, 120] as const
const AP_DUNG = ['khach_moi', 'khach_hay_vang', 'moi_lich'] as const

/** Mã neo của thẻ tài khoản — nút "Khai tài khoản nhận tiền" ở tờ thu cuộn tới đây. */
export const MA_THE_TAI_KHOAN = 'the-tai-khoan-nhan-tien'

export function TheCaiDatCoc({
  api,
  onLoi,
  onXong,
  cuon = false,
  onDaCuon,
}: {
  api: Api
  onLoi: (e: unknown) => void
  onXong: (cau: string) => void
  /** Tờ thu bảo "Khai tài khoản nhận tiền": cuộn tới thẻ tài khoản sau khi tải xong, rồi báo `onDaCuon` (chỉ cuộn một lần). */
  cuon?: boolean
  onDaCuon?: () => void
}) {
  const [c, setC] = useState<CauHinhCoc | null>(null)
  // Thẻ tài khoản đang gõ dở mà chưa bấm "Lưu tài khoản" → thẻ cọc nhắc ngay trên nút của mình (B1: bấm nhầm nút cuối trang).
  const [chuaLuuTaiKhoan, setChuaLuuTaiKhoan] = useState(false)

  useEffect(() => {
    let con = true
    api.coc
      .cauHinh()
      .then((r) => {
        if (con) setC(r)
      })
      .catch(onLoi)
    return () => {
      con = false
    }
  }, [api, onLoi])

  const daTai = c !== null
  useEffect(() => {
    if (!cuon || !daTai) return
    document.getElementById(MA_THE_TAI_KHOAN)?.scrollIntoView({ block: 'start' })
    onDaCuon?.()
  }, [cuon, daTai, onDaCuon])

  if (c === null) return null
  return (
    <>
      <TheTaiKhoanNhanTien
        api={api}
        c={c}
        onDoiTaiKhoan={(tk) =>
          setC((truoc) => (truoc === null ? truoc : { ...truoc, taiKhoan: tk }))
        }
        onGoDo={setChuaLuuTaiKhoan}
        onLoi={onLoi}
        onXong={onXong}
      />
      <TheDatCoc
        api={api}
        c={c}
        chuaLuuTaiKhoan={chuaLuuTaiKhoan}
        onDoc={setC}
        onLoi={onLoi}
        onXong={onXong}
      />
    </>
  )
}

function TheTaiKhoanNhanTien({
  api,
  c,
  onDoiTaiKhoan,
  onGoDo,
  onLoi,
  onXong,
}: {
  api: Api
  c: CauHinhCoc
  onDoiTaiKhoan: (tk: CauHinhCoc['taiKhoan']) => void
  /** Báo thẻ cọc: đang có chữ gõ dở chưa lưu (`taiKhoanGoDo`). */
  onGoDo: (goDo: boolean) => void
  onLoi: (e: unknown) => void
  onXong: (cau: string) => void
}) {
  // Chưa có tài khoản → ba ô mở sẵn. Đã có → một dòng tóm tắt, bấm "Đổi tài khoản" mới hiện ô (ẩn mặc định).
  const [dangSua, setDangSua] = useState(false)
  const [bin, setBin] = useState(c.taiKhoan?.bin ?? '')
  const [soTk, setSoTk] = useState(c.taiKhoan?.soTaiKhoan ?? '')
  const [tenTk, setTenTk] = useState(c.taiKhoan?.tenTaiKhoan ?? '')
  const [ma, setMa] = useState('')
  const [dang, setDang] = useState(false)
  const [loi, setLoi] = useState<string | null>(null)
  // Khoá chống lặp RIÊNG của thẻ này: cùng endpoint với thẻ cọc nhưng thân khác.
  const [khoa, lamMoiKhoa] = useKhoaIdem()

  const nhap = { bin, soTaiKhoan: soTk, tenTaiKhoan: tenTk }
  const hienO = c.taiKhoan === null || dangSua
  const hienMa = c.taiKhoan === null || canMaKhiLuu(c.taiKhoan, nhap)
  const goDo = taiKhoanGoDo(c.taiKhoan, nhap, ma)
  useEffect(() => {
    onGoDo(goDo)
  }, [goDo, onGoDo])

  function dienTu(tk: CauHinhCoc['taiKhoan']) {
    setBin(tk?.bin ?? '')
    setSoTk(tk?.soTaiKhoan ?? '')
    setTenTk(tk?.tenTaiKhoan ?? '')
    setMa('')
    setLoi(null)
  }

  async function luu() {
    setDang(true)
    setLoi(null)
    try {
      // Đọc lại NGAY trước khi lưu: trường cọc gửi kèm là của bản vừa đọc, không đè thay đổi cọc từ máy khác.
      const vuaDoc = await api.coc.cauHinh()
      await api.coc.luu(thanLuuTaiKhoan(vuaDoc, nhap, ma), khoa)
      lamMoiKhoa()
      // Đọc lại: máy chủ viết tên tài khoản IN HOA không dấu — chủ tiệm thấy đúng chữ khách sẽ thấy. Chỉ cập nhật phần tài khoản.
      const moi = await api.coc.cauHinh()
      onDoiTaiKhoan(moi.taiKhoan)
      dienTu(moi.taiKhoan)
      setDangSua(false)
      onXong(T.accountSaved)
    } catch (e) {
      // Mất mạng: GIỮ khoá — bấm lại là đúng yêu cầu cũ.
      if (!(e instanceof ApiFailure) || e.code !== 'OFFLINE') lamMoiKhoa()
      setLoi(e instanceof ApiFailure ? e.messageVi : T.serverFailed)
    } finally {
      setDang(false)
    }
  }

  return (
    <section id={MA_THE_TAI_KHOAN} className="the the-dem the-khoi the-tai-khoan">
      <h2>{T.accountTitle}</h2>
      <p className="chu-phu">{T.accountHint}</p>
      {!hienO && c.taiKhoan !== null ? (
        <>
          <p className="tk-tom-tat">
            <CacCum
              cac={[
                c.nganHang.find((n) => n.bin === c.taiKhoan?.bin)?.ten ?? c.taiKhoan.bin,
                c.taiKhoan.soTaiKhoan,
                c.taiKhoan.tenTaiKhoan,
              ]}
            />
          </p>
          <button
            type="button"
            className="nut nut-vien"
            onClick={() => {
              dienTu(c.taiKhoan)
              setDangSua(true)
            }}
          >
            {T.accountChange}
          </button>
        </>
      ) : (
        <>
          <div className="ds-truong">
            <div className="truong">
              <label className="nhan" htmlFor="coc-ngan-hang">
                {T.depositSetBank}
              </label>
              <select
                id="coc-ngan-hang"
                className="o-chon"
                value={bin}
                disabled={dang}
                onChange={(e) => setBin(e.target.value)}
              >
                <option value="">{T.depositSetBankPick}</option>
                {c.nganHang.map((n) => (
                  <option key={n.bin} value={n.bin}>
                    {n.ten}
                  </option>
                ))}
              </select>
            </div>
            <div className="truong">
              <label className="nhan" htmlFor="coc-so-tk">
                {T.depositSetAccountNo}
              </label>
              <input
                id="coc-so-tk"
                className="o-nhap"
                inputMode="numeric"
                value={soTk}
                maxLength={19}
                disabled={dang}
                onChange={(e) => setSoTk(e.target.value.replace(/[^0-9A-Za-z]/g, ''))}
              />
            </div>
            <div className="truong">
              <label className="nhan" htmlFor="coc-ten-tk">
                {T.depositSetAccountName}
              </label>
              <input
                id="coc-ten-tk"
                className="o-nhap"
                value={tenTk}
                maxLength={50}
                disabled={dang}
                onChange={(e) => setTenTk(e.target.value)}
              />
            </div>
            {hienMa && (
              <div className="truong">
                <label className="nhan" htmlFor="coc-ma">
                  {T.depositSetPin}
                </label>
                <input
                  id="coc-ma"
                  className="o-nhap"
                  type="password"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={ma}
                  disabled={dang}
                  onChange={(e) => setMa(e.target.value)}
                />
                <span className="chu-phu">{T.accountPinHint}</span>
              </div>
            )}
          </div>
          {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
          <button
            type="button"
            className="nut nut-chinh"
            disabled={dang || !coTheLuuTaiKhoan(c.taiKhoan, nhap, ma)}
            onClick={() => void luu()}
          >
            {dang ? T.saving : T.accountSave}
          </button>
          {c.taiKhoan !== null && (
            <button
              type="button"
              className="nut nut-vien"
              disabled={dang}
              onClick={() => {
                dienTu(c.taiKhoan)
                setDangSua(false)
              }}
            >
              {T.accountCancel}
            </button>
          )}
        </>
      )}
    </section>
  )
}

function TheDatCoc({
  api,
  c,
  chuaLuuTaiKhoan,
  onDoc,
  onLoi,
  onXong,
}: {
  api: Api
  c: CauHinhCoc
  /** Thẻ tài khoản đang gõ dở chưa lưu: nhắc ngay trên nút của thẻ này. */
  chuaLuuTaiKhoan: boolean
  onDoc: (r: CauHinhCoc) => void
  onLoi: (e: unknown) => void
  onXong: (cau: string) => void
}) {
  const [bat, setBat] = useState(c.bat)
  const [apDung, setApDung] = useState<CauHinhCoc['apDung']>(c.apDung)
  const [soTien, setSoTien] = useState(c.soTienVnd ?? '')
  const [giuPhut, setGiuPhut] = useState(c.giuPhut)
  const [chinhSach, setChinhSach] = useState(c.chinhSach ?? '')
  const [dang, setDang] = useState(false)
  const [khoa, lamMoiKhoa] = useKhoaIdem()

  const dien = useCallback((r: CauHinhCoc) => {
    setBat(r.bat)
    setApDung(r.apDung)
    setSoTien(r.soTienVnd ?? '')
    setGiuPhut(r.giuPhut)
    setChinhSach(r.chinhSach ?? '')
  }, [])

  const daCoTaiKhoan = c.taiKhoan !== null
  const form: FormCoc = { bat, apDung, soTien, giuPhut, chinhSach }
  const hopLe = coTheBatCoc(form, daCoTaiKhoan)
  // Nút khoá khi form bằng bản đang lưu: không còn nút luôn bật mà bấm vào chỉ ghi lại đúng thứ đã có rồi báo "đã lưu".
  const daDoi = cocDaDoi(form, c)

  async function luu() {
    setDang(true)
    try {
      // `taiKhoan: null` = giữ nguyên tài khoản đang lưu (thẻ bên trên lo việc khai / đổi).
      await api.coc.luu(thanLuuCoc(form), khoa)
      lamMoiKhoa()
      const moi = await api.coc.cauHinh()
      dien(moi)
      onDoc(moi)
      onXong(T.depositSaved)
    } catch (e) {
      // Mất mạng: GIỮ khoá — bấm lại là đúng yêu cầu cũ.
      if (!(e instanceof ApiFailure) || e.code !== 'OFFLINE') lamMoiKhoa()
      onLoi(e)
    } finally {
      setDang(false)
    }
  }

  return (
    <section id="the-dat-coc" className="the the-dem the-khoi">
      <h2>{T.depositSetTitle}</h2>
      <p className="chu-phu">{T.depositSetHint}</p>
      <div className="ds-cong-tac">
        <CongTac bat={bat} tat={dang} nhan={T.depositSetOn} onDoi={setBat} />
      </div>
      {bat && !daCoTaiKhoan && <ThongBao loai="canh-bao">{T.depositNeedAccount}</ThongBao>}
      {/* Tắt cọc: chỉ còn công tắc — chủ tiệm không phải đọc qua bảy ô không dùng tới. */}
      {bat && (
        <div className="ds-truong">
          <div className="truong">
            <span className="nhan">{T.depositSetWho}</span>
            <div className="hang-chip chia-deu cot-3">
              {AP_DUNG.map((a) => (
                <button
                  key={a}
                  type="button"
                  aria-pressed={apDung === a}
                  className={`chip ${apDung === a ? 'dang-chon' : ''}`}
                  onClick={() => setApDung(a)}
                >
                  {T.depositSetWhoLabel(a)}
                </button>
              ))}
            </div>
          </div>
          <div className="truong">
            <label className="nhan" htmlFor="coc-so-tien">
              {T.depositSetAmount}
            </label>
            <OTien id="coc-so-tien" giaTri={soTien} onDoi={setSoTien} goiY="100.000" />
          </div>
          <div className="truong">
            <span className="nhan">{T.depositSetHold}</span>
            <div className="hang-chip chia-deu cot-4">
              {GIU_CHON.map((p) => (
                <button
                  key={p}
                  type="button"
                  aria-pressed={giuPhut === p}
                  className={`chip ${giuPhut === p ? 'dang-chon' : ''}`}
                  onClick={() => setGiuPhut(p)}
                >
                  {T.depositSetMinutes(p)}
                </button>
              ))}
            </div>
          </div>
          <div className="truong">
            <label className="nhan" htmlFor="coc-chinh-sach">
              {T.depositSetPolicy}
            </label>
            <textarea
              id="coc-chinh-sach"
              className="o-nhap"
              rows={3}
              maxLength={500}
              placeholder={T.depositSetPolicyHint}
              value={chinhSach}
              onChange={(e) => setChinhSach(e.target.value)}
            />
          </div>
        </div>
      )}
      {chuaLuuTaiKhoan && <ThongBao loai="canh-bao">{T.accountUnsaved}</ThongBao>}
      <button
        type="button"
        className="nut nut-chinh"
        disabled={dang || !hopLe || !daDoi}
        onClick={() => void luu()}
      >
        {dang ? T.saving : T.depositSave}
      </button>
    </section>
  )
}
